import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { api, brl, catLabel, CATEGORIES, errMsg, fmtDate, img } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Field, inputCls } from "@/components/landing/ProposalForm";
import { QrPoster } from "@/components/panel/QrPoster";

const toForm = (ev) => ({
  title: ev?.title || "", category: ev?.category || "social", date: ev?.date || "", location: ev?.location || "", description: ev?.description || "",
  price_per_photo: ev ? (ev.price_per_photo_cents ?? 0) / 100 : "", photographer_id: ev?.photographer_id || "", status: ev?.status || "published",
});

export const EventForm = ({ initial, user, onSaved, submitLabel }) => {
  const [f, setF] = useState(toForm(initial));
  const [busy, setBusy] = useState(false);
  const { data: team } = useFetch(user.role === "admin" ? "/panel/photographers" : null, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, price_per_photo: Number(f.price_per_photo || 0), photographer_id: f.photographer_id || null };
      const res = initial ? await api.patch(`/panel/events/${initial.id}`, body) : await api.post("/panel/events", body);
      toast.success("Evento salvo");
      onSaved?.(res.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-[1.5rem] border border-white/10 bg-ink-2 p-6 sm:grid-cols-2" data-testid="event-form">
      <Field label="Nome do evento" className="sm:col-span-2"><input required value={f.title} onChange={set("title")} className={inputCls} data-testid="event-title-input" /></Field>
      <Field label="Categoria">
        <select value={f.category} onChange={set("category")} className={inputCls} data-testid="event-category-select">
          {CATEGORIES.map((c) => <option key={c.id} value={c.id} className="bg-ink">{c.label}</option>)}
        </select>
      </Field>
      <Field label="Data"><input type="date" value={f.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} data-testid="event-date-input" /></Field>
      <Field label="Local"><input value={f.location} onChange={set("location")} className={inputCls} data-testid="event-location-input" /></Field>
      <Field label="Valor por foto (R$)"><input required type="number" min="0" step="0.5" value={f.price_per_photo} onChange={set("price_per_photo")} className={inputCls} data-testid="event-price-input" /></Field>
      {user.role === "admin" && (
        <Field label="Fotógrafo responsável">
          <select value={f.photographer_id} onChange={set("photographer_id")} className={inputCls} data-testid="event-photographer-select">
            <option value="" className="bg-ink">Eu mesmo</option>
            {(Array.isArray(team) ? team : []).filter((u) => u.id !== user.id).map((u) => <option key={u.id} value={u.id} className="bg-ink">{u.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Visibilidade">
        <select value={f.status} onChange={set("status")} className={inputCls} data-testid="event-status-select">
          <option value="published" className="bg-ink">Publicado</option>
          <option value="draft" className="bg-ink">Rascunho (oculto)</option>
          <option value="archived" className="bg-ink">Arquivado</option>
        </select>
      </Field>
      <Field label="Descrição" className="sm:col-span-2"><textarea rows={3} value={f.description} onChange={set("description")} className={inputCls} data-testid="event-description-input" /></Field>
      <button disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-flash px-6 py-3 font-semibold text-ink disabled:opacity-60 sm:justify-self-start" data-testid="event-save-btn">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {submitLabel || "Salvar"}
      </button>
    </form>
  );
};

const EventDetail = ({ id, user, back }) => {
  const { data: ev, reload } = useFetch(`/panel/events/${id}`);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let created = 0;
    try {
      for (let i = 0; i < list.length; i += 5) {
        const fd = new FormData();
        list.slice(i, i + 5).forEach((f) => fd.append("files", f));
        const { data } = await api.post(`/panel/events/${id}/photos`, fd);
        created += data?.created ?? 0;
        setProgress(Math.min(100, Math.round(((i + 5) / list.length) * 100)));
        reload();
      }
      toast.success(`${created} foto(s) publicada(s) com marca d'água`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };
  const remove = async (pid) => {
    await api.delete(`/panel/photos/${pid}`).catch((e) => toast.error(errMsg(e)));
    reload();
  };

  if (!ev) return <p className="text-haze">Carregando…</p>;
  const photos = Array.isArray(ev.photos) ? ev.photos : [];
  return (
    <div className="space-y-8" data-testid="event-detail">
      <div className="flex flex-wrap items-center gap-4">
        <button onClick={back} className="inline-flex items-center gap-2 text-sm text-haze hover:text-flash" data-testid="event-detail-back-btn"><ArrowLeft className="h-4 w-4" /> Voltar</button>
        <a href={`/eventos/${ev.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-flash" data-testid="event-public-link">Ver galeria pública <ExternalLink className="h-4 w-4" /></a>
      </div>
      <QrPoster path={`/eventos/${ev.slug}`} title={ev.title} fileName={ev.slug} testid="event-qr" />
      <label className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-[1.5rem] border-2 border-dashed border-flash/40 bg-flash/5 p-10 text-center transition-colors hover:bg-flash/10 ${busy ? "pointer-events-none opacity-70" : ""}`}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }} data-testid="photo-dropzone">
        {busy ? <Loader2 className="h-8 w-8 animate-spin text-flash" /> : <Upload className="h-8 w-8 text-flash" />}
        <span className="font-semibold">{busy ? `Enviando e aplicando marca d'água… ${progress}%` : "Arraste as fotos aqui ou clique para enviar"}</span>
        <span className="text-xs text-haze">JPG/PNG até 30MB cada. As prévias públicas recebem marca d'água; o original só é liberado após o pagamento.</span>
        <input type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="photo-upload-input" />
      </label>
      <div>
        <p className="mb-4 text-sm text-haze">{photos.length} foto(s) publicada(s)</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
          {photos.map((p) => (
            <div key={p.id} className="group relative overflow-hidden rounded-xl border border-white/10">
              <img src={img("thumb", p.id)} alt={p.code} className="aspect-square w-full object-cover" loading="lazy" />
              <span className="absolute bottom-1.5 left-1.5 rounded bg-ink/80 px-1.5 font-mono text-[11px]">{p.code}</span>
              <button onClick={() => remove(p.id)} className="absolute right-1.5 top-1.5 rounded-full bg-ink/80 p-2 text-red-300 opacity-100 sm:opacity-0 sm:group-hover:opacity-100" data-testid={`photo-delete-${p.code}`} aria-label="Remover"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="mb-4 font-display text-xl font-bold">Dados do evento</h3>
        <EventForm initial={ev} user={user} onSaved={reload} />
      </div>
    </div>
  );
};

export const PanelEvents = ({ user }) => {
  const { data, reload } = useFetch("/panel/events", []);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const events = Array.isArray(data) ? data : [];

  if (openId) return <EventDetail id={openId} user={user} back={() => { setOpenId(null); reload(); }} />;
  return (
    <div className="space-y-6" data-testid="panel-events">
      <button onClick={() => setCreating((c) => !c)} className="inline-flex items-center gap-2 rounded-full bg-flash px-5 py-3 font-semibold text-ink" data-testid="new-event-btn"><Plus className="h-4 w-4" /> Novo evento</button>
      {creating && <EventForm user={user} submitLabel="Criar evento" onSaved={(d) => { setCreating(false); reload(); if (d?.id) setOpenId(d.id); }} />}
      <div className="grid gap-3">
        {events.map((ev) => (
          <button key={ev.id} onClick={() => setOpenId(ev.id)} data-testid={`panel-event-${ev.slug}`}
            className="flex items-center gap-4 rounded-2xl border border-white/10 bg-ink-2 p-3 text-left transition-colors hover:border-flash/40">
            <div className="h-16 w-20 shrink-0 overflow-hidden rounded-xl bg-ink-3">{ev.cover_photo_id && <img src={img("thumb", ev.cover_photo_id)} alt="" className="h-full w-full object-cover" />}</div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{ev.title}</p>
              <p className="truncate text-xs text-haze">{catLabel(ev.category)} · {fmtDate(ev.date)} · {ev.photo_count} fotos · {brl(ev.price_per_photo_cents)}/foto · {ev.photographer_name}</p>
            </div>
            <span className={`hidden rounded-full px-3 py-1 text-xs sm:inline ${ev.status === "published" ? "bg-emerald-400/15 text-emerald-300" : "bg-white/10 text-haze"}`}>{ev.status === "published" ? "Publicado" : "Rascunho"}</span>
          </button>
        ))}
        {!events.length && <p className="text-haze" data-testid="panel-events-empty">Nenhum evento ainda. Crie o primeiro.</p>}
      </div>
    </div>
  );
};
